import React from 'react';
import { cloneDeep } from 'lodash';
import {
  getContextDataFromSessionStorage,
  getMesssagesForLocale,
  getSessionStorageKey,
  LOCALE_EN,
  setContextDataToSessionStorage,
} from '../../util/applicationContext';

const DEFAULT_LOCALE = LOCALE_EN;
const DEFAULT_LOGGEDIN_USER = 'nobody';
const DEFAULT_CONTEXT = {
  locale: DEFAULT_LOCALE,
  messages: getMesssagesForLocale(DEFAULT_LOCALE),
  userStats: {
    [DEFAULT_LOGGEDIN_USER]: {
      loginCount: 0,
    },
  },
  currentLoggedInUser: DEFAULT_LOGGEDIN_USER,
};

export const ApplicationContext = React.createContext(
  getContextDataFromSessionStorage() || cloneDeep(DEFAULT_CONTEXT),
);

function appContextReducer(state, action) {
  switch (action.type) {
    case 'SET_LOCALE': {
      const updatedState = {
        ...state,
        locale: action.payload,
        messages: getMesssagesForLocale(action.payload),
      };
      setContextDataToSessionStorage(updatedState);
      return updatedState;
    }
    case 'SET_USER_LOGIN_COUNT': {
      const user = action.payload;
      let loginCount = 0;
      if (user in state.userStats) {
        loginCount = state.userStats[user].loginCount + 1;
      } else {
        loginCount = 1;
      }
      const updatedState = {
        ...state,
        userStats: {
          ...state.userStats,
          [user]: {
            loginCount,
          },
        },
      };
      setContextDataToSessionStorage(updatedState);
      return updatedState;
    }
    default: {
      throw new Error(`Unhandled application context action type: ${action.type}`);
    }
  }
}
export function ApplicationContextProvider({ children }) {
  const [contextState, dispatch] = React.useReducer(
    appContextReducer,
    getContextDataFromSessionStorage() || cloneDeep(DEFAULT_CONTEXT),
  );
  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context
  const value = { contextState, dispatch };
  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}

export function ApplicationContextConsumer({ children }) {
  return (
    <ApplicationContext.Consumer>
      {context => {
        if (context === undefined) {
          throw new Error(
            'ApplicationContextConsumer must be used within a ApplicationContextProvider',
          );
        }
        return children(context);
      }}
    </ApplicationContext.Consumer>
  );
}

export function useApplicationContext() {
  const context = React.useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useApplicationContext must be used within a ApplicationContextProvider');
  }
  return context;
}
